import { CheckCircle } from '@untitledui/icons';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { CloseButton } from '@/lib/base/buttons/close-button';
import { cx } from '@/utils/cx';

import { PROPOSAL_TOAST_POSITION } from './toastPosition';

const TOAST_OPTIONS = {
  duration: 5000,
  position: PROPOSAL_TOAST_POSITION,
  // On desktop (lg+), offset above the sticky footer. On mobile, no offset needed.
  className: 'lg:mb-16',
  // Override Sonner's default wrapper styles so only the inner card renders
  // (prevents double-border from the Toaster's default border/padding/shadow).
  style: {
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    padding: 0,
  },
};

/**
 * Show a success toast when a background chat turn on an action finishes.
 * Unlike showSuccessToast, this does not imply anything was executed — the
 * action was updated and is ready for the user to review and approve.
 */
export function showChatTurnCompleteToast({
  orgId,
  proposalId,
  proposalTitle,
}: {
  orgId: number;
  proposalId: number;
  proposalTitle?: string;
}): void {
  toast.custom(
    t => (
      <div
        role='status'
        aria-live='polite'
        aria-atomic='true'
        className={cx(
          'flex items-start gap-3 p-3.5 bg-bg-primary border border-border-secondary rounded-xl shadow-md min-w-[320px] max-w-[420px]'
        )}
      >
        <div className='flex items-center justify-center size-9 rounded-full bg-success-50 shrink-0 mt-0.5'>
          <CheckCircle className='size-5 text-success-600' aria-hidden='true' />
        </div>

        <div className='flex-1 min-w-0 space-y-1'>
          <p className='text-sm font-semibold text-fg-primary'>
            Action updated
          </p>
          <p className='text-sm text-fg-secondary'>Ready for your review</p>
          {proposalTitle && (
            <p className='text-sm text-fg-secondary truncate'>
              {proposalTitle}
            </p>
          )}
          <div className='pt-1'>
            <Button
              size='sm'
              color='secondary'
              onClick={() => {
                window.location.assign(`/${orgId}/actions/${proposalId}`);
                toast.dismiss(t);
              }}
            >
              View
            </Button>
          </div>
        </div>

        <CloseButton
          size='xs'
          label='Dismiss'
          onPress={() => toast.dismiss(t)}
          className='shrink-0'
        />
      </div>
    ),
    TOAST_OPTIONS
  );
}
